import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { foliosApi } from '../../lib/api';
import GatewayPaymentDialog from './GatewayPaymentDialog';
import { useAuth } from '../../lib/auth-context';
import { format } from 'date-fns';
import { 
  Plus, DollarSign, CreditCard, Receipt, Ban, Printer,
  FileText, CheckCircle, XCircle, ArrowRight, User, Building2
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function FoliosPage() {
  const { currentProperty } = useAuth();
  const [folios, setFolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolio, setSelectedFolio] = useState(null);
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [gatewayDialogOpen, setGatewayDialogOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [chargeCodes, setChargeCodes] = useState([]);
  const [filter, setFilter] = useState('OPEN');

  const fetchFolios = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await foliosApi.list(currentProperty.id, params);
      setFolios(response.data || []);
    } catch (error) {
      toast.error('Failed to load folios');
    } finally {
      setLoading(false);
    }
  }, [currentProperty, filter]);

  const loadFolioDetails = async (folio) => {
    try {
      const response = await foliosApi.get(folio.id);
      setSelectedFolio(response.data);
    } catch (error) {
      toast.error('Failed to load folio details');
    }
  };

  const loadChargeCodes = async () => {
    try {
      const response = await foliosApi.getChargeCodes(currentProperty.id);
      setChargeCodes(response.data || []);
    } catch (error) {
      console.error('Failed to load charge codes');
    }
  };

  useEffect(() => {
    fetchFolios();
    loadChargeCodes();
  }, [fetchFolios]);

  const handlePostCharge = async (chargeData) => {
    if (!selectedFolio) return;
    try {
      await foliosApi.postCharge(selectedFolio.id, chargeData);
      toast.success('Charge posted successfully');
      setChargeDialogOpen(false);
      loadFolioDetails(selectedFolio);
      fetchFolios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post charge');
    }
  };

  const handlePostPayment = async (paymentData) => {
    if (!selectedFolio) return;
    try {
      await foliosApi.postPayment(selectedFolio.id, paymentData);
      toast.success('Payment posted successfully');
      setPaymentDialogOpen(false);
      loadFolioDetails(selectedFolio);
      fetchFolios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post payment');
    }
  };

  const handleVoidTransaction = async (reason) => {
    if (!selectedTransaction) return;
    try {
      await foliosApi.voidTransaction(selectedTransaction.id, { reason });
      toast.success('Transaction voided');
      setVoidDialogOpen(false);
      setSelectedTransaction(null);
      loadFolioDetails(selectedFolio);
      fetchFolios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void transaction');
    }
  };

  const handleSettleFolio = async () => {
    if (!selectedFolio) return;
    try {
      await foliosApi.settle(selectedFolio.id);
      toast.success('Folio settled successfully');
      setSelectedFolio(null);
      fetchFolios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to settle folio');
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'CHARGE': return <Receipt className="h-4 w-4 text-red-500" />;
      case 'PAYMENT': return <CreditCard className="h-4 w-4 text-green-500" />;
      case 'ADJUSTMENT': return <ArrowRight className="h-4 w-4 text-amber-500" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="folios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Folio Management</h1>
          <p className="text-muted-foreground">Manage guest folios, charges, and payments for {currentProperty?.name}</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Folio List */}
        <div className="col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Folios</CardTitle>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="SETTLED">Settled</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-auto">
                {loading ? (
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
                  </div>
                ) : folios.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground" data-testid="no-folios">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No {filter !== 'all' ? filter.toLowerCase() : ''} folios found</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {folios.map((folio) => (
                      <div
                        key={folio.id}
                        className={cn(
                          "p-4 cursor-pointer hover:bg-muted/50 transition-colors",
                          selectedFolio?.id === folio.id && "bg-muted"
                        )}
                        onClick={() => loadFolioDetails(folio)}
                        data-testid={`folio-${folio.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-sm font-medium">{folio.folio_number}</span>
                          <Badge variant={folio.status === 'OPEN' ? 'default' : 'secondary'}>
                            {folio.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {folio.guests?.first_name} {folio.guests?.last_name}
                          {folio.reservations?.confirmation_number && (
                            <span className="ml-2 font-mono">({folio.reservations.confirmation_number})</span>
                          )}
                        </div>
                        <div className="mt-1 text-lg font-semibold">
                          ETB {((folio.total_charges || 0) - (folio.total_payments || 0) + (folio.total_adjustments || 0)).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Folio Details */}
        <div className="col-span-8">
          {!selectedFolio ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a folio to view details</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <span className="font-mono">{selectedFolio.folio_number}</span>
                      <Badge variant={selectedFolio.status === 'OPEN' ? 'default' : 'secondary'}>
                        {selectedFolio.status}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      {selectedFolio.reservations?.guest_name} - Room #{selectedFolio.reservations?.rooms?.room_number}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Balance</div>
                    <div className={cn(
                      "text-2xl font-bold",
                      selectedFolio.calculated_balance > 0 ? "text-destructive" : "text-green-600"
                    )}>
                      ETB {selectedFolio.calculated_balance?.toLocaleString() || '0'}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-red-50 rounded-lg">
                    <div className="text-sm text-red-600">Charges</div>
                    <div className="text-lg font-semibold text-red-700">ETB {(selectedFolio.total_charges || 0).toLocaleString()}</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-600">Payments</div>
                    <div className="text-lg font-semibold text-green-700">ETB {(selectedFolio.total_payments || 0).toLocaleString()}</div>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <div className="text-sm text-amber-600">Adjustments</div>
                    <div className="text-lg font-semibold text-amber-700">ETB {(selectedFolio.total_adjustments || 0).toLocaleString()}</div>
                  </div>
                </div>

                {/* Actions */}
                {selectedFolio.status === 'OPEN' && (
                  <div className="flex gap-2">
                    <Button onClick={() => setChargeDialogOpen(true)} data-testid="post-charge-btn">
                      <Plus className="h-4 w-4 mr-2" /> Post Charge
                    </Button>
                    <Button variant="outline" onClick={() => setPaymentDialogOpen(true)} data-testid="post-payment-btn">
                      <CreditCard className="h-4 w-4 mr-2" /> Post Payment
                    </Button>
                    <Button variant="outline" onClick={() => setGatewayDialogOpen(true)} data-testid="gateway-payment-btn">
                      <CreditCard className="h-4 w-4 mr-2" /> Pay via Gateway
                    </Button>
                    <Button variant="outline">
                      <Printer className="h-4 w-4 mr-2" /> Print
                    </Button>
                  </div>
                )}

                <Separator />

                {/* Transactions */}
                <div>
                  <h4 className="font-medium mb-3">Transactions</h4>
                  <Table data-testid="transactions-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!selectedFolio.transactions || selectedFolio.transactions.length === 0) ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            No transactions yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedFolio.transactions.map((trans) => (
                          <TableRow key={trans.id} className={cn(trans.is_void && "opacity-50 line-through")}>
                            <TableCell className="text-sm">{trans.transaction_date}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getTransactionIcon(trans.transaction_type)}
                                <span>{trans.transaction_type}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>{trans.description}</div>
                              {trans.is_void && <Badge variant="destructive" className="text-xs">VOID</Badge>}
                            </TableCell>
                            <TableCell className={cn(
                              "text-right font-medium",
                              trans.transaction_type === 'CHARGE' ? "text-red-600" : "text-green-600"
                            )}>
                              {trans.transaction_type === 'CHARGE' ? '+' : '-'} ETB {trans.total_amount?.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {!trans.is_void && selectedFolio.status === 'OPEN' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTransaction(trans);
                                    setVoidDialogOpen(true);
                                  }}
                                >
                                  <Ban className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
              {selectedFolio.status === 'OPEN' && (
                <CardFooter className="border-t pt-4">
                  <Button 
                    className="w-full" 
                    disabled={selectedFolio.calculated_balance > 0.01}
                    onClick={handleSettleFolio}
                    data-testid="settle-folio-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" /> Settle & Close Folio
                  </Button>
                </CardFooter>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Post Charge Dialog */}
      <Dialog open={chargeDialogOpen} onOpenChange={setChargeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Post Charge</DialogTitle>
            <DialogDescription>Add a charge to folio {selectedFolio?.folio_number}</DialogDescription>
          </DialogHeader>
          <ChargeForm chargeCodes={chargeCodes} onSubmit={handlePostCharge} onCancel={() => setChargeDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Post Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Post Payment</DialogTitle>
            <DialogDescription>Record payment for folio {selectedFolio?.folio_number}</DialogDescription>
          </DialogHeader>
          <PaymentForm 
            balance={selectedFolio?.calculated_balance || 0}
            onSubmit={handlePostPayment} 
            onCancel={() => setPaymentDialogOpen(false)} 
          />
        </DialogContent>
      </Dialog>

      {/* Void Transaction Dialog */}
      <Dialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Transaction</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <VoidForm 
            transaction={selectedTransaction}
            onSubmit={handleVoidTransaction} 
            onCancel={() => {
              setVoidDialogOpen(false);
              setSelectedTransaction(null);
            }} 
          />
        </DialogContent>
      </Dialog>

      <GatewayPaymentDialog
        open={gatewayDialogOpen}
        onOpenChange={setGatewayDialogOpen}
        propertyId={currentProperty?.id}
        folio={selectedFolio}
        onPaid={() => selectedFolio && loadFolioDetails(selectedFolio)}
      />
    </div>
  );
}

function ChargeForm({ chargeCodes, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    description: '',
    quantity: 1,
    unit_amount: '',
    tax_amount: 0,
    charge_code_id: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      quantity: parseFloat(formData.quantity),
      unit_amount: parseFloat(formData.unit_amount),
      tax_amount: parseFloat(formData.tax_amount || 0),
    });
  };

  const handleChargeCodeChange = (codeId) => {
    const code = chargeCodes.find(c => c.id === codeId);
    setFormData({
      ...formData,
      charge_code_id: codeId,
      description: code?.name || '',
      unit_amount: code?.default_amount || '',
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="charge-form">
      <div className="space-y-2">
        <Label>Charge Code</Label>
        <Select value={formData.charge_code_id} onValueChange={handleChargeCodeChange}>
          <SelectTrigger><SelectValue placeholder="Select charge code..." /></SelectTrigger>
          <SelectContent>
            {chargeCodes.map((code) => (
              <SelectItem key={code.id} value={code.id}>
                {code.name} ({code.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Description *</Label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          data-testid="charge-description"
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Quantity</Label>
          <Input
            type="number"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            min="1"
            step="0.01"
          />
        </div>
        <div className="space-y-2">
          <Label>Unit Amount *</Label>
          <Input
            type="number"
            value={formData.unit_amount}
            onChange={(e) => setFormData({ ...formData, unit_amount: e.target.value })}
            required
            min="0"
            step="0.01"
            data-testid="charge-amount"
          />
        </div>
        <div className="space-y-2">
          <Label>Tax</Label>
          <Input
            type="number"
            value={formData.tax_amount}
            onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
            min="0"
            step="0.01"
          />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="charge-submit-btn">Post Charge</Button>
      </div>
    </form>
  );
}

function PaymentForm({ balance, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    amount: balance > 0 ? balance : '',
    payment_method: 'CASH',
    reference: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="payment-form">
      <div className="space-y-2">
        <Label>Amount *</Label>
        <Input
          type="number"
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          required
          min="0.01"
          step="0.01"
          data-testid="payment-amount"
        />
        {balance > 0 && (
          <p className="text-sm text-muted-foreground">Outstanding balance: ETB {balance.toLocaleString()}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label>Payment Method *</Label>
        <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="CASH">Cash</SelectItem>
            <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
            <SelectItem value="DEBIT_CARD">Debit Card</SelectItem>
            <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
            <SelectItem value="MOBILE_MONEY">Mobile Money</SelectItem>
            <SelectItem value="COMPANY_AR">Company AR</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Reference</Label>
        <Input
          value={formData.reference}
          onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
          placeholder="Receipt/Transaction number"
        />
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="payment-submit-btn">Post Payment</Button>
      </div>
    </form>
  );
}

function VoidForm({ transaction, onSubmit, onCancel }) {
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(reason);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="void-form">
      <div className="p-4 bg-destructive/10 rounded-lg">
        <p className="text-sm">You are about to void:</p>
        <p className="font-medium">{transaction?.description}</p>
        <p className="text-lg font-bold">ETB {transaction?.total_amount?.toLocaleString()}</p>
      </div>
      <div className="space-y-2">
        <Label>Reason for voiding *</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          placeholder="Enter reason..."
          data-testid="void-reason"
        />
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="destructive" data-testid="void-submit-btn">Void Transaction</Button>
      </div>
    </form>
  );
}
